import { format as formatWithPattern, isValid, parse } from 'date-fns';
import { enUS, fr, type Locale } from 'date-fns/locale';

/**
 * Dates typed into a date field. French writes day first (`12/10/2026`),
 * English month first (`10/12/2026`), matching the `Oct 12, 2026` / `12 oct.
 * 2026` the portals display. ISO (`2026-10-12`) is accepted in both, as is
 * the displayed text itself, so a value can be edited where it stands.
 */
type TypedDateLanguage = 'fr' | 'en';

/** Why a typed date was refused. Each maps to `date_picker.errors.<key>`. */
export type TypedDateError =
  'invalid' | 'before_min' | 'after_max' | 'unavailable';

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

function languageOf(locale?: string): TypedDateLanguage {
  return locale?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

const DATE_FNS_LOCALES: Record<TypedDateLanguage, Locale> = { fr, en: enUS };

/** Formats a written date can take besides the numeric ones, per language. */
const TEXT_PATTERNS: Record<TypedDateLanguage, string[]> = {
  fr: ['d MMM yyyy', 'd MMMM yyyy'],
  en: ['MMM d, yyyy', 'MMMM d, yyyy', 'MMM d yyyy', 'MMMM d yyyy'],
};

/** The numeric pattern a user types, as date-fns tokens. */
export function typedDatePattern(locale?: string): string {
  return languageOf(locale) === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
}

/** A date written the way the user types it (`12/10/2026` in French). */
export function formatTypedDate(date: Date, locale?: string): string {
  return formatWithPattern(date, typedDatePattern(locale));
}

/** A real calendar day, or null (31/02, month 13, year 20260). */
function calendarDay(year: number, month: number, day: number): Date | null {
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/** A two-digit year lands within 50 years of today, like date-fns `yy`. */
function fullYear(raw: string, reference: Date): number {
  if (raw.length !== 2) return Number(raw);
  const referenceYear = reference.getFullYear();
  let year = Math.floor(referenceYear / 100) * 100 + Number(raw);
  if (year > referenceYear + 50) year -= 100;
  else if (year <= referenceYear - 50) year += 100;
  return year;
}

/**
 * Reads a typed date. Returns null for anything that is not one real day.
 *
 * Accepted, in both languages: `2026-10-12`, `2026/10/12`, the language's
 * numeric order with `/`, `-`, `.` or a space between the parts (`12/10/2026`
 * in French, `10/12/2026` in English, two-digit years too), eight digits in
 * a row (`12102026`), and the written date the field displays.
 */
export function parseTypedDate(
  input: string,
  locale?: string,
  reference: Date = new Date()
): Date | null {
  const text = input.trim().replace(/\s+/g, ' ');
  if (!text) return null;
  const language = languageOf(locale);

  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(text);
  if (iso) return calendarDay(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const numeric = /^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{4}|\d{2})$/.exec(text);
  if (numeric) {
    const [first, second] = [Number(numeric[1]), Number(numeric[2])];
    const year = fullYear(numeric[3], reference);
    return language === 'fr'
      ? calendarDay(year, second, first)
      : calendarDay(year, first, second);
  }

  const compact = /^(\d{2})(\d{2})(\d{4})$/.exec(text);
  if (compact) {
    const [first, second] = [Number(compact[1]), Number(compact[2])];
    const year = Number(compact[3]);
    const localeOrder =
      language === 'fr'
        ? calendarDay(year, second, first)
        : calendarDay(year, first, second);
    if (localeOrder) return localeOrder;
    return calendarDay(
      Number(text.slice(0, 4)),
      Number(text.slice(4, 6)),
      Number(text.slice(6, 8))
    );
  }

  // French writes the first of the month "1er" ("1er oct. 2026").
  const written = language === 'fr' ? text.replace(/\b1er\b/i, '1') : text;
  for (const pattern of TEXT_PATTERNS[language]) {
    const parsed = parse(written, pattern, reference, {
      locale: DATE_FNS_LOCALES[language],
    });
    if (isValid(parsed)) {
      return calendarDay(
        parsed.getFullYear(),
        parsed.getMonth() + 1,
        parsed.getDate()
      );
    }
  }
  return null;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Whether a parsed date breaks the field's bounds. Compared by day, so a
 * `minDate` of "now" still allows today. `isUnavailable` covers the field's
 * other disabled days (weekends, holidays) after the bounds.
 */
export function typedDateBoundsError(
  date: Date,
  bounds: {
    minDate?: Date;
    maxDate?: Date;
    isUnavailable?: (date: Date) => boolean;
  }
): TypedDateError | null {
  const day = startOfDay(date);
  if (bounds.minDate && day < startOfDay(bounds.minDate)) return 'before_min';
  if (bounds.maxDate && day > startOfDay(bounds.maxDate)) return 'after_max';
  if (bounds.isUnavailable?.(day)) return 'unavailable';
  return null;
}
